// src/Context.tsx
import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  Dispatch,
  SetStateAction,
} from 'react';
import { User } from './models/user.model';

interface AppState {
  user: User | null;
  currentSelections: {
    createdAt: Date;
    validTo: Date;
    selections: any[];
  } | null;
}

interface ContextType {
  state: AppState;
  setState: Dispatch<SetStateAction<AppState>>;
}

// A corrupt or hand-edited value must not stop the app from loading.
const readStoredJson = (key: string) => {
  try {
    const value = localStorage.getItem(key);
    return value ? JSON.parse(value) : null;
  } catch {
    localStorage.removeItem(key);
    return null;
  }
};

const initialState: AppState = {
  user: readStoredJson('user'),
  currentSelections: readStoredJson('currentSelections'),
};

const Context = createContext<ContextType | undefined>(undefined);

export const useContextValue = (): ContextType => {
  const context = useContext(Context);

  if (!context) {
    throw new Error('useContextValue must be used within a Provider');
  }

  return context;
};

interface ProviderProps {
  children: ReactNode;
}

export const Provider: React.FC<ProviderProps> = ({ children }) => {
  const [state, setState] = useState<AppState>(initialState);

  return (
    <Context.Provider value={{ state, setState }}>{children}</Context.Provider>
  );
};
